import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { MeResponse, Role } from '@shared/api-types';
import type { AccountId } from '@shared/ids';
import { isApiClientError } from '../lib/api';
import { useMe } from '../lib/query';
import { useBranding } from '../lib/branding';
import { ErrorBanner, Loading } from '../components/Status';
import { SignInPage } from './SignInPage';

const SessionContext = createContext<MeResponse | null>(null);

/** Sesja jest gwarantowana wewnatrz `AuthGate` — dlatego hook nie zwraca `null`. */
export function useSession(): MeResponse {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession() poza <AuthGate>');
  }
  return value;
}

export function useRole(): Role | null {
  return useSession().role;
}

export function useAccountId(): AccountId | null {
  return useSession().activeAccountId;
}

/**
 * Hierarchia rol z `middleware/tenant.ts`: `owner` przechodzi wszedzie, `admin`
 * dziedziczy uprawnienia zarzadcze POZA `approver` (wymog compliance dla pharmy).
 * UI tylko UKRYWA to, czego serwer nie pozwoli zrobic — autorytetem jest Worker.
 */
export function hasRole(role: Role | null, ...allowed: readonly Role[]): boolean {
  if (role === null) return false;
  if (role === 'owner') return true;
  if (allowed.includes(role)) return true;
  if (role === 'admin') return allowed.some((candidate) => candidate !== 'approver');
  return false;
}

/**
 * Brama sesji. 401 z `GET /api/me` to normalny stan (uzytkownik nie jest
 * zalogowany), nie awaria — pokazujemy ekran logowania, nie blad.
 */
export function AuthGate({ children }: { readonly children: ReactNode }) {
  const me = useMe();
  useBranding(me.data?.branding ?? null);

  if (me.isPending) {
    return (
      <main className="flex min-h-full items-center justify-center p-8">
        <Loading />
      </main>
    );
  }

  if (me.isError) {
    if (isApiClientError(me.error) && me.error.status === 401) {
      return <SignInPage />;
    }
    return (
      <main className="mx-auto max-w-md p-8">
        <ErrorBanner error={me.error} />
      </main>
    );
  }

  return <SessionContext.Provider value={me.data}>{children}</SessionContext.Provider>;
}

export default AuthGate;
